import { useCallback } from 'react';
import { useEditModeStore } from '../stores/editModeStore';
import type { EditModeStore } from '../stores/editModeStore';

/**
 * Custom hook for accessing and managing edit mode state
 * Provides a convenient interface for components to interact with the edit mode store
 */
export const useEditMode = () => {
  const store = useEditModeStore();

  // Memoized callbacks to prevent unnecessary re-renders
  const toggleEditMode = useCallback(() => {
    store.toggleEditMode();
  }, [store.toggleEditMode]);

  const setEditMode = useCallback((enabled: boolean) => {
    store.setEditMode(enabled);
  }, [store.setEditMode]);

  const setUserRole = useCallback((role: 'internal' | 'external' | null) => {
    store.setUserRole(role);
  }, [store.setUserRole]);

  const setComponentEditMode = useCallback((componentId: string, enabled: boolean) => {
    store.setComponentEditMode(componentId, enabled);
  }, [store.setComponentEditMode]);

  const getComponentEditMode = useCallback((componentId: string) => {
    return store.getComponentEditMode(componentId);
  }, [store.getComponentEditMode]);

  const addTableEdit = useCallback((tableName: string, recordId: string) => {
    store.addTableEdit(tableName, recordId);
  }, [store.addTableEdit]);

  const removeTableEdit = useCallback((tableName: string, recordId: string) => {
    store.removeTableEdit(tableName, recordId);
  }, [store.removeTableEdit]);

  const clearTableEdits = useCallback((tableName?: string) => {
    store.clearTableEdits(tableName);
  }, [store.clearTableEdits]);

  const hasTableEdits = useCallback((tableName: string) => {
    return store.hasTableEdits(tableName);
  }, [store.hasTableEdits]);

  const getTableEditCount = useCallback((tableName: string) => {
    return store.getTableEditCount(tableName);
  }, [store.getTableEditCount]);

  const addToHistory = useCallback((
    entry: Omit<EditModeStore['editHistory'][0], 'timestamp'>
  ) => {
    store.addToHistory(entry);
  }, [store.addToHistory]);

  const clearHistory = useCallback(() => {
    store.clearHistory();
  }, [store.clearHistory]);

  const updateSettings = useCallback((settings: Partial<EditModeStore['settings']>) => {
    store.updateSettings(settings);
  }, [store.updateSettings]);

  const resetEditMode = useCallback(() => {
    store.resetEditMode();
  }, [store.resetEditMode]);

  const resetAllEdits = useCallback(() => {
    store.resetAllEdits();
  }, [store.resetAllEdits]);

  return {
    // State
    isEditMode: store.isEditMode,
    userRole: store.userRole,
    componentEditStates: store.componentEditStates,
    activeTableEdits: store.activeTableEdits,
    settings: store.settings,
    editHistory: store.editHistory,

    // Actions
    toggleEditMode,
    setEditMode,
    setUserRole,
    setComponentEditMode,
    getComponentEditMode,
    addTableEdit,
    removeTableEdit,
    clearTableEdits,
    hasTableEdits,
    getTableEditCount,
    addToHistory,
    clearHistory,
    updateSettings,
    resetEditMode,
    resetAllEdits,
  };
};

/**
 * Hook for components that need to know if they should be editable
 * Combines global edit mode state with component-specific settings
 */
export const useCanEdit = (componentId?: string) => {
  const isEditMode = useEditModeStore((state) => state.isEditMode);
  const userRole = useEditModeStore((state) => state.userRole);
  const getComponentEditMode = useEditModeStore((state) => state.getComponentEditMode);

  // External users can never edit, regardless of edit mode
  if (userRole === 'external') {
    return false;
  }

  // Internal users can edit when edit mode is enabled
  if (userRole === 'internal') {
    return componentId ? getComponentEditMode(componentId) : isEditMode;
  }

  // If no user role is set, default to edit mode state
  return componentId ? getComponentEditMode(componentId) : isEditMode;
};

/**
 * Hook for table-specific edit mode functionality
 * Provides convenient methods for managing edits on specific tables
 */
export const useTableEditMode = (tableName: string) => {
  const addTableEdit = useEditModeStore((state) => state.addTableEdit);
  const removeTableEdit = useEditModeStore((state) => state.removeTableEdit);
  const clearTableEdits = useEditModeStore((state) => state.clearTableEdits);
  const hasTableEdits = useEditModeStore((state) => state.hasTableEdits);
  const getTableEditCount = useEditModeStore((state) => state.getTableEditCount);
  const addToHistory = useEditModeStore((state) => state.addToHistory);

  const canEdit = useCanEdit();

  const startEdit = useCallback((recordId: string) => {
    if (!canEdit) return;

    addTableEdit(tableName, recordId);
    addToHistory({
      action: 'start_edit',
      tableName,
      recordId,
      changes: {},
    });
  }, [canEdit, addTableEdit, addToHistory, tableName]);

  const finishEdit = useCallback((recordId: string, changes: Record<string, unknown>) => {
    if (!canEdit) return;

    removeTableEdit(tableName, recordId);
    addToHistory({
      action: 'finish_edit',
      tableName,
      recordId,
      changes,
    });
  }, [canEdit, removeTableEdit, addToHistory, tableName]);

  const cancelEdit = useCallback((recordId: string) => {
    removeTableEdit(tableName, recordId);
    addToHistory({
      action: 'cancel_edit',
      tableName,
      recordId,
      changes: {},
    });
  }, [removeTableEdit, addToHistory, tableName]);

  const clearAllEdits = useCallback(() => {
    clearTableEdits(tableName);
  }, [clearTableEdits, tableName]);

  return {
    canEdit,
    hasEdits: hasTableEdits(tableName),
    editCount: getTableEditCount(tableName),
    startEdit,
    finishEdit,
    cancelEdit,
    clearAllEdits,
  };
};

/**
 * Hook for monitoring edit mode changes and side effects
 * Useful for components that need to react to edit mode transitions
 */
export const useEditModeEffects = () => {
  const settings = useEditModeStore((state) => state.settings);

  return {
    shouldConfirmOnExit: settings.confirmOnExit,
    autoSaveEnabled: settings.autoSave,
    persistAcrossSessions: settings.persistAcrossSessions,
    isTransitioning: false, // Could add transition state if needed
  };
};